import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Prompt } from 'react-router-dom';
import { resetProject } from '../../../store/actions/project';
import IconButton from '@cayuse/shared-react.components.icon-button';
import TextArea from '@cayuse/shared-react.components.form.inputs.text-area';
import { isNonEmptyString } from '@cayuse/shared-react.utils.type-checks';
import { notify } from '@cayuse/shared-react.components.banner-notification';
import { validateTitleLength } from 'utils';
import { useDictionary } from '../../../hooks';
import { api } from '../../../services';
import styles from './styles.css';


const EditableTitle = (props) => {
    const { title, isAdmin, projectId } = props;

    const [isEditMode, setEditMode] = useState(false);
    const [isUpdating, setUpdating] = useState(false);
    const [isNotValid, setNotValid] = useState(false);
    const [inputTitle, setInputTitle] = useState("");

    const dictionary = useDictionary("projects.summary");
    const textareaRef = useRef(null);

    useEffect(() => {
        setInputTitle(title);
    }, [title]);

    useEffect(() => {
        if (textareaRef.current === null) {
            textareaRef.current = document.getElementById("title-text-area");
        } else {
            textareaRef.current.style.height = "0px";

            textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
        }

        const keyDownHandler = (event) => {
            if (event.key === 'Enter' && document.activeElement === textareaRef.current) {
                event.preventDefault();

                if (!isNotValid && !isUpdating) {
                    submitChanges();
                }
            }

            if (event.key === 'Escape') {
                event.preventDefault();

                discardChanges();
            }
        };

        window.onbeforeunload = () => isEditMode ? true : undefined;

        if (isEditMode) {
            textareaRef.current.focus();

            document.addEventListener('keydown', keyDownHandler);
        }

        return () => {
            document.removeEventListener('keydown', keyDownHandler);

            window.onbeforeunload = undefined;
        };
    }, [isUpdating, isEditMode, isNotValid, inputTitle]);

    const handleInput = (value) => {
        setNotValid(!isNonEmptyString(value) || !validateTitleLength(value))
        setInputTitle(value);
    };

    const submitChanges = () => {
        if (inputTitle !== title) {
            setUpdating(true);

            api.project.updateTitle({ id: projectId }, { projectTitle: inputTitle })
                .then(() => {
                    const message = dictionary.notifications.updateSuccess || "Your changes have been saved. Updates may take several minutes to display.";

                    notify(message, {
                        type: "success",
                        autoClose: true,
                    });

                    resetProject();
                })
                .catch(() => {
                    discardChanges();
                });
        } else {
            discardChanges();
        }
    };

    const discardChanges = useCallback(() => {
        setInputTitle(title);

        setEditMode(false);

        setUpdating(false);

        setNotValid(false);
    }, []);

    return (
        <>
            <span className={styles.label}>{dictionary.title || "Title:"}</span>
            <div className={styles.titleTextArea__Container}>
                <div className={styles.titleTextArea__Wrapper}>
                    <TextArea
                        id="title-text-area"
                        className={styles.titleTextArea}
                        disabled={!isEditMode}
                        value={inputTitle}
                        rows={1}
                        onChange={(e) => handleInput(e.target.value)}
                    />
                    {isEditMode &&
                        <span className={styles.caption}>
                            {dictionary.editDescription ?? "Any changes made will affect all records within the project"}
                        </span>
                    }
                </div>
            </div>
            {isAdmin &&
                <div className={styles.iconsWrapper}>
                    {isEditMode &&
                        <>
                            <IconButton icon="check" tertiary onClick={submitChanges} disabled={isUpdating || isNotValid} />
                            <IconButton icon="times" tertiary onClick={discardChanges} disabled={isUpdating}  />
                            <Prompt
                                when={isEditMode}
                                message={dictionary.leaveEditPrompt ?? "Changes you made may not be saved"}
                            />
                        </>
                    }
                    {!isEditMode &&
                        <IconButton icon="pencil" tertiary onClick={() => setEditMode(prevState => !prevState)} />
                    }
                </div>
            }
            {isEditMode && !validateTitleLength(inputTitle) && <div className={styles.titleLengthWarning}>
                {dictionary.notifications.projectTitleLengthValidation ?? "Project title must be less than 256 characters."}
            </div>}
        </>
    );
};

export default EditableTitle;
